import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface BaseProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

/** Shared behaviour: Esc to close, body scroll lock, focus the panel, restore focus on close. */
function useOverlay(open: boolean, onClose: () => void) {
  const panel = useRef<HTMLDivElement>(null);
  // Keep the latest handler in a ref so re-renders (e.g. typing in a form inside the dialog) never re-run the
  // effect below, which would re-focus the panel and steal focus from the input being typed in.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [open]);
  return panel;
}

function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
      <h2 className="text-lg font-bold">{title}</h2>
      <button onClick={onClose} aria-label="Close" className="grid size-9 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground">
        <X className="size-5" />
      </button>
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: BaseProps & { size?: 'sm' | 'md' | 'lg' }) {
  const panel = useOverlay(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[70] grid place-items-end sm:place-items-center">
      <div className="anim-fade absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        ref={panel} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}
        className={cn(
          'anim-slide-up relative flex max-h-[92dvh] w-full flex-col rounded-t-3xl bg-card shadow-pop outline-none sm:m-4 sm:rounded-3xl',
          size === 'sm' && 'sm:max-w-sm', size === 'md' && 'sm:max-w-lg', size === 'lg' && 'sm:max-w-3xl',
        )}
      >
        <Header title={title} onClose={onClose} />
        <div className="overflow-y-auto p-5">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-4">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({ open, onClose, title, children, footer, side = 'right' }: BaseProps & { side?: 'left' | 'right' }) {
  const panel = useOverlay(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[70]">
      <div className="anim-fade absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        ref={panel} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}
        className={cn(
          'absolute inset-y-0 flex w-[min(24rem,92vw)] flex-col bg-card shadow-pop outline-none',
          side === 'right' ? 'anim-slide-right right-0' : 'anim-slide-left left-0',
        )}
      >
        <Header title={title} onClose={onClose} />
        <div className="flex-1 overflow-y-auto">{children}</div>
        {footer && <div className="border-t border-border p-4">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
